// Shared layout for content pages (About, product pages): breadcrumb, header,
// feature cards, and a visible FAQ. Content renders without entrance
// animations so it is visible on first paint and in the prerendered HTML.

import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const Page = styled.main`
  flex: 1;
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #1A365D 0%, #0B1D3A 100%);
  color: var(--white);
`;

export const Container = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: var(--spacing-lg) var(--spacing-md) var(--spacing-xxl);
`;

const BreadcrumbNav = styled.nav`
  margin-bottom: var(--spacing-lg);
  font-size: 0.9rem;

  ol {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-xs);
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    color: rgba(255, 255, 255, 0.7);
  }

  li + li::before {
    content: '/';
    margin-right: var(--spacing-xs);
    color: rgba(255, 255, 255, 0.4);
  }

  a {
    color: var(--accent);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const Breadcrumb = ({ items }) => (
  <BreadcrumbNav aria-label="Breadcrumb">
    <ol>
      {items.map((item, index) => (
        <li key={item.label}>
          {index < items.length - 1 ? (
            <Link to={item.to}>{item.label}</Link>
          ) : (
            <span aria-current="page">{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbNav>
);

export const Header = styled.header`
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-md);
  }
`;

export const HeaderLogo = styled.img`
  width: 96px;
  height: 96px;
  flex-shrink: 0;
  border-radius: 22px;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.08);
  padding: 8px;
`;

export const Eyebrow = styled.p`
  margin: 0 0 var(--spacing-xs);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $color }) => $color || 'var(--accent)'};
`;

export const Title = styled.h1`
  margin: 0 0 var(--spacing-sm);
  font-size: clamp(2rem, 5vw, 3rem);
  line-height: 1.15;
  color: var(--white);
`;

export const Lead = styled.p`
  margin: 0;
  font-size: 1.15rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.88);
  max-width: 720px;
`;

export const Section = styled.section`
  margin-top: var(--spacing-xl);
  padding: 0;
`;

export const SectionTitle = styled.h2`
  margin: 0 0 var(--spacing-md);
  font-size: 1.6rem;
  color: var(--white);
`;

export const Prose = styled.div`
  p {
    color: rgba(255, 255, 255, 0.86);
    line-height: 1.8;
    margin: 0 0 var(--spacing-md);
  }

  a {
    color: var(--accent);
  }
`;

export const CardGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--spacing-md);
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Card = styled.li`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);

  h3 {
    margin: 0 0 var(--spacing-xs);
    font-size: 1.1rem;
    color: var(--white);

    a {
      color: inherit;
      text-decoration: none;
    }

    a:hover {
      color: var(--accent);
    }
  }

  p {
    margin: 0;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.8);
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
`;

export const StoreBadge = styled.a`
  display: inline-flex;

  img {
    height: 48px;
    width: auto;
  }
`;

export const PrimaryLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 0.8rem 1.5rem;
  border-radius: var(--border-radius-md);
  background: ${({ $color }) => $color || 'var(--accent)'};
  color: var(--white);
  font-weight: 600;
  text-decoration: none;

  &:hover {
    filter: brightness(1.08);
  }
`;

export const InlineLinks = styled.p`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin: var(--spacing-md) 0 0;

  a {
    color: var(--accent);
    font-weight: 500;
  }
`;

const FaqList = styled.div`
  display: grid;
  gap: var(--spacing-sm);
`;

const FaqItem = styled.details`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md) var(--spacing-lg);

  summary {
    cursor: pointer;
    font-weight: 600;
    color: var(--white);
    list-style-position: outside;
  }

  summary h3 {
    display: inline;
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--white);
  }

  p {
    margin: var(--spacing-sm) 0 0;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.84);
  }
`;

// Answers stay in the DOM (closed <details>), so crawlers read the full text.
export const Faq = ({ items }) => (
  <FaqList>
    {items.map((item, index) => (
      <FaqItem key={item.question} open={index === 0}>
        <summary>
          <h3>{item.question}</h3>
        </summary>
        <p>{item.answer}</p>
      </FaqItem>
    ))}
  </FaqList>
);
